/**
 * Single source of truth for rendering an instant as text. Every screen and
 * export (Admin, History, Home, meeting detail, feedback list,
 * transcript/.md/.txt export, .docx meeting minutes) renders dates through
 * these three functions.
 *
 * `formatDateTime` / `formatTime` stay pinned to `dd/MM/yyyy, HH:mm` / `HH:mm`
 * regardless of UI language (BRAI-226/BRAI-252: web display and downloaded
 * files must not drift when the viewer's language changes).
 *
 * `formatDate` follows the UI locale so the selected value in date pickers
 * matches `datePlaceholder` (`yyyy/mm/dd` for Japanese, `dd/mm/yyyy` otherwise).
 */

type DateInput = number | string | Date

// `en-GB` is only used to pin the digit order (dd/MM/yyyy) and separators;
// combined with `hourCycle: 'h23'` this makes date+time / time output
// independent of the `locale` callers pass in, and independent of the browser
// engine's default hour-cycle-per-locale choice.
const FIXED_FORMAT_LOCALE = 'en-GB'
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
}
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}
const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
}

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value)
}

/** Maps UI locale to an Intl locale that yields the designed digit order. */
function dateFormatLocale(locale: string): string {
  return locale === 'ja' ? 'ja-JP' : FIXED_FORMAT_LOCALE
}

/**
 * Formats an instant as date + time (e.g. `23/07/2026, 14:30`). The `_locale`
 * parameter is accepted (and still passed by every caller) but intentionally
 * unused — the format is fixed regardless of UI/export language, see the
 * module doc comment. Returns `'—'` for a missing/invalid value so callers
 * never need their own null guard.
 */
export function formatDateTime(value: DateInput | null | undefined, _locale: string): string {
  if (value == null) return '—'
  return new Intl.DateTimeFormat(FIXED_FORMAT_LOCALE, DATE_TIME_OPTIONS).format(toDate(value))
}

/**
 * Formats an instant as a date only. Digit order follows `locale` so it matches
 * `datePlaceholder`: Japanese → `yyyy/MM/dd` (e.g. `23/07/2026` becomes
 * `2026/07/23`); other locales stay `dd/MM/yyyy`.
 */
export function formatDate(value: DateInput | null | undefined, locale: string): string {
  if (value == null) return '—'
  return new Intl.DateTimeFormat(dateFormatLocale(locale), DATE_OPTIONS).format(toDate(value))
}

/** Formats an instant as a time only (e.g. `14:30`). See {@link formatDateTime}. */
export function formatTime(value: DateInput | null | undefined, _locale: string): string {
  if (value == null) return '—'
  return new Intl.DateTimeFormat(FIXED_FORMAT_LOCALE, TIME_OPTIONS).format(toDate(value))
}
